// src/pages/OrderStatusPage.jsx
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress, Typography, Box, Button, Divider } from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL;

const OrderStatusPage = () => {
    const { token } = useParams();
    const [status, setStatus] = useState("loading"); // loading | ok | error
    const [order, setOrder] = useState(null);

    useEffect(() => {
        if (!token) {
            setStatus("error");
            return;
        }

        let cancelled = false;

        const fetchStatus = async () => {
            try {
                const res = await fetch(`${API_URL}/order-status/${encodeURIComponent(token)}`);
                if (!res.ok) {
                    throw new Error("not found");
                }
                const data = await res.json();
                if (!cancelled) {
                    setOrder(data.data);
                    setStatus("ok");
                }
            } catch (err) {
                if (!cancelled) {
                    setStatus("error");
                }
            }
        };

        fetchStatus();

        return () => {
            cancelled = true;
        };
    }, [token]);

    if (status === "loading") {
        return (
            <Box maxWidth="600px" mx="auto" mt={6} textAlign="center">
                <CircularProgress />
                <Typography mt={2}>Sprawdzamy status zamówienia...</Typography>
            </Box>
        );
    }

    // Deliberately the same message regardless of whether the link is
    // malformed, unknown, or expired — see backend order.statusByToken.
    if (status === "error") {
        return (
            <Box maxWidth="600px" mx="auto" mt={6} textAlign="center">
                <Typography variant="h5" color="error">
                    Nie znaleziono zamówienia lub link wygasł.
                </Typography>
                <Typography mt={2} color="text.secondary">
                    Sprawdź, czy link został skopiowany w całości, lub skontaktuj się z obsługą sklepu.
                </Typography>
                <Button variant="contained" sx={{ mt: 3 }} href="/">
                    Wróć do strony głównej
                </Button>
            </Box>
        );
    }

    return (
        <Box maxWidth="600px" mx="auto" mt={6}>
            <Typography variant="h4" textAlign="center" gutterBottom>
                Zamówienie #{order.id}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 3 }}>
                {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : ""}
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h6">{order.friendlyStatus?.label}</Typography>

            <Box sx={{ mt: 2, display: "grid", rowGap: 1 }}>
                <Typography variant="body2">Kwota: {order.totalAmount} zł</Typography>
                {order.items?.length > 0 && (
                    <Typography variant="body2">
                        Produkty: {order.items.map((item, idx) => (
                            <span key={item.id ?? idx}>{item.title} (x{item.quantity}), </span>
                        ))}
                    </Typography>
                )}
                {order.trackingNumber && (
                    <Typography variant="body2">
                        Numer przesyłki: {order.trackingNumber}{" "}
                        <a
                            href={`https://www.dhl.com/pl-pl/home/tracking/tracking-parcel.html?submit=1&tracking-id=${order.trackingNumber}`}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Śledź przesyłkę
                        </a>
                    </Typography>
                )}
                {order.estimatedDeliveryDate && (
                    <Typography variant="body2">
                        Szacowana dostawa: {new Date(order.estimatedDeliveryDate).toLocaleDateString()}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default OrderStatusPage;
